import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Fab,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CameraAlt as CameraIcon,
  PhotoLibrary as PhotoLibraryIcon,
  SchoolOutlined,
  School,
  SchoolSharp,
  SchoolTwoTone,
} from '@mui/icons-material';
import { Photo, PhotoCategorie, Etablissement } from '../types';
import { airtableService } from '../services/airtableService';
import { uploadToCloudinary, CloudinaryResponse } from '../services/cloudinaryService';

interface PhotoManagerProps {
  etablissement: Etablissement;
}

interface PhotoFormData {
  categorie: PhotoCategorie;
  sousCategorie: string;
  legende: string;
  description: string;
  photoFiles?: File[];
}

export const PhotoManager: React.FC<PhotoManagerProps> = ({ etablissement }) => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState<Photo | null>(null);
  const [formData, setFormData] = useState<PhotoFormData>({
    categorie: PhotoCategorie.FACADE,
    sousCategorie: '',
    legende: '',
    description: '',
    photoFiles: [],
  });
  const [error, setError] = useState<string | null>(null);
  const fabRef = useRef<HTMLButtonElement>(null);
  const categorieRef = useRef<HTMLInputElement>(null);

  // Charger les photos au montage
  useEffect(() => {
    console.log('Submission ID de l\'établissement sélectionné :', etablissement.submissionId);
    loadPhotos();
  }, [etablissement.submissionId]);

  useEffect(() => {
    if (openDialog) {
      setTimeout(() => categorieRef.current?.focus(), 0);
    }
  }, [openDialog]);

  const loadPhotos = async () => {
    try {
      setLoading(true);
      const data = await airtableService.getPhotosEtablissement(etablissement.submissionId);
      setPhotos(data);
    } catch (error) {
      console.error('Erreur lors du chargement des photos:', error);
      setError('Erreur lors du chargement des photos');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPhoto = () => {
    setEditingPhoto(null);
    setFormData({
      categorie: PhotoCategorie.FACADE,
      sousCategorie: '',
      legende: '',
      description: '',
      photoFiles: [],
    });
    setOpenDialog(true);
  };

  const handleEditPhoto = (photo: Photo) => {
    setEditingPhoto(photo);
    setFormData({
      categorie: photo.categorie,
      sousCategorie: photo.sousCategorie || '',
      legende: photo.legende,
      description: photo.description || '',
    });
    setOpenDialog(true);
  };

  const handleDeletePhoto = async (photoId: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette photo ?')) {
      try {
        await airtableService.deletePhoto(photoId);
        setPhotos(photos.filter(p => p.id !== photoId));
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        setError('Erreur lors de la suppression de la photo');
      }
    }
  };

  // Fonction pour détecter si on est sur mobile ou desktop
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // Remplacer la fonction handlePhotoCapture par une version qui permet la prise de plusieurs photos consécutives
  const handlePhotoCapture = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    // Sur mobile : utiliser la caméra arrière, sur desktop : utiliser la webcam
    if (isMobileDevice()) {
      input.capture = 'environment'; // Caméra arrière sur mobile
    } else {
      input.capture = 'user'; // Webcam sur desktop (Mac, PC)
    }
    
    input.onchange = (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files && files.length > 0) {
        setFormData(prev => ({
          ...prev,
          photoFiles: prev.photoFiles ? [...prev.photoFiles, ...Array.from(files)] : Array.from(files)
        }));
      }
    };
    input.click();
  };

  // Fonction alternative pour utiliser directement la webcam sur desktop
  const handleWebcamCapture = async () => {
    // Vérifier si l'API getUserMedia est supportée
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.warn('getUserMedia non supporté, utilisation de la méthode de fallback');
      handlePhotoCapture();
      return;
    }

    try {
      // Demander l'accès à la webcam
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'user', // Webcam frontale
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        } 
      });

      // Créer un élément vidéo temporaire
      const video = document.createElement('video');
      video.srcObject = stream;
      video.autoplay = true;
      video.style.position = 'fixed';
      video.style.top = '0';
      video.style.left = '0';
      video.style.width = '100%';
      video.style.height = '100%';
      video.style.zIndex = '9999';
      video.style.backgroundColor = 'black';

      // Créer un canvas pour capturer l'image
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.style.position = 'fixed';
      canvas.style.top = '50%';
      canvas.style.left = '50%';
      canvas.style.transform = 'translate(-50%, -50%)';
      canvas.style.zIndex = '10000';
      canvas.style.border = '2px solid white';
      canvas.style.borderRadius = '8px';

      // Créer les boutons de contrôle
      const controls = document.createElement('div');
      controls.style.position = 'fixed';
      controls.style.bottom = '20px';
      controls.style.left = '50%';
      controls.style.transform = 'translateX(-50%)';
      controls.style.zIndex = '10001';
      controls.style.display = 'flex';
      controls.style.gap = '10px';

      const captureBtn = document.createElement('button');
      captureBtn.textContent = '📸 Capturer';
      captureBtn.style.padding = '10px 20px';
      captureBtn.style.backgroundColor = '#4CAF50';
      captureBtn.style.color = 'white';
      captureBtn.style.border = 'none';
      captureBtn.style.borderRadius = '5px';
      captureBtn.style.cursor = 'pointer';

      const cancelBtn = document.createElement('button');
      cancelBtn.textContent = '❌ Annuler';
      cancelBtn.style.padding = '10px 20px';
      cancelBtn.style.backgroundColor = '#f44336';
      cancelBtn.style.color = 'white';
      cancelBtn.style.border = 'none';
      cancelBtn.style.borderRadius = '5px';
      cancelBtn.style.cursor = 'pointer';

      // Fonction de nettoyage
      const cleanup = () => {
        stream.getTracks().forEach(track => track.stop());
        document.body.removeChild(video);
        document.body.removeChild(canvas);
        document.body.removeChild(controls);
      };

      // Gestionnaire pour capturer l'image
      captureBtn.onclick = () => {
        if (ctx && video.videoWidth > 0) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          ctx.drawImage(video, 0, 0);
          
          // Convertir le canvas en fichier
          canvas.toBlob((blob) => {
            if (blob) {
              const file = new File([blob], `webcam_${Date.now()}.jpg`, { type: 'image/jpeg' });
              setFormData(prev => ({
                ...prev,
                photoFiles: prev.photoFiles ? [...prev.photoFiles, file] : [file]
              }));
            }
          }, 'image/jpeg', 0.9);
        }
        cleanup();
      };

      // Gestionnaire pour annuler
      cancelBtn.onclick = cleanup;

      // Ajouter les éléments au DOM
      document.body.appendChild(video);
      document.body.appendChild(canvas);
      controls.appendChild(captureBtn);
      controls.appendChild(cancelBtn);
      document.body.appendChild(controls);

      // Attendre que la vidéo soit prête
      video.onloadedmetadata = () => {
        video.play();
      };

    } catch (error) {
      console.error('Erreur lors de l\'accès à la webcam:', error);
      // Fallback vers la méthode standard
      handlePhotoCapture();
    }
  };

  const handlePhotoSelect = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files && files.length > 0) {
        setFormData(prev => ({ ...prev, photoFiles: Array.from(files) }));
      }
    };
    input.click();
  };

  const handleSubmit = async () => {
    if (!formData.legende.trim()) {
      setError('La légende est obligatoire');
      return;
    }

    if ((!formData.photoFiles || formData.photoFiles.length === 0) && !editingPhoto) {
      setError('Veuillez sélectionner au moins une photo');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (editingPhoto) {
        // Mise à jour (on ne gère qu'une seule image en édition)
        let photoUrl = editingPhoto.photoUrl;
        let cloudinaryMetadata = editingPhoto.photoMetadata;
        if (formData.photoFiles && formData.photoFiles.length > 0) {
          try {
            const cloudinaryResponse = await uploadToCloudinary(formData.photoFiles[0]);
            photoUrl = cloudinaryResponse.secure_url;
            cloudinaryMetadata = cloudinaryResponse;
          } catch (err) {
            console.error('Erreur détaillée Cloudinary:', err);
            setError("Erreur lors de l'upload de la photo sur Cloudinary");
            setLoading(false);
            return;
          }
        }
        
        // Vérifier si updatePhoto supporte les métadonnées
        const updateData: any = {
          categorie: formData.categorie,
          sousCategorie: formData.sousCategorie,
          legende: formData.legende,
          description: formData.description,
          photoUrl,
        };
        
        // Ajouter les métadonnées seulement si la méthode les supporte
        if (cloudinaryMetadata) {
          updateData.photoMetadata = cloudinaryMetadata;
        }
        
        const updatedPhoto = await airtableService.updatePhoto(editingPhoto.id, updateData);
        setPhotos(photos.map(p => p.id === editingPhoto.id ? updatedPhoto : p));
      } else if (formData.photoFiles) {
        // Ajout multiple
        const newPhotos: Photo[] = [];
        for (const file of formData.photoFiles) {
          try {
            const cloudinaryResponse = await uploadToCloudinary(file);
            
            // Vérifier si la nouvelle méthode existe (production vs local)
            let newPhoto: Photo;
            if (typeof airtableService.addPhotoWithMetadata === 'function') {
              // Version locale avec métadonnées
              newPhoto = await airtableService.addPhotoWithMetadata({
                etablissementId: etablissement.submissionId,
                categorie: formData.categorie,
                sousCategorie: formData.sousCategorie,
                legende: formData.legende,
                description: formData.description,
                photoUrl: cloudinaryResponse.secure_url,
              }, cloudinaryResponse);
            } else {
              // Version production sans métadonnées (fallback)
              console.log('⚠️ Utilisation de la méthode de fallback (production)');
              newPhoto = await airtableService.addPhoto({
                etablissementId: etablissement.submissionId,
                categorie: formData.categorie,
                sousCategorie: formData.sousCategorie,
                legende: formData.legende,
                description: formData.description,
                photoUrl: cloudinaryResponse.secure_url,
              });
              
              // Tentative d'ajout des métadonnées via updatePhoto
              if (typeof airtableService.updatePhoto === 'function') {
                try {
                  await airtableService.updatePhoto(newPhoto.id, {
                    photoMetadata: cloudinaryResponse
                  });
                  console.log('✅ Métadonnées ajoutées via updatePhoto');
                } catch (metadataError) {
                  console.warn('⚠️ Impossible d\'ajouter les métadonnées:', metadataError);
                }
              }
            }
            
            newPhotos.push(newPhoto);
          } catch (err) {
            console.error('Erreur détaillée Cloudinary:', err);
            setError("Erreur lors de l'upload de la photo sur Cloudinary");
            setLoading(false);
            return;
          }
        }
        setPhotos([...photos, ...newPhotos]);
      }

      setOpenDialog(false);
      setFormData({
        categorie: PhotoCategorie.FACADE,
        sousCategorie: '',
        legende: '',
        description: '',
        photoFiles: [],
      });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setError('Erreur lors de la sauvegarde de la photo');
    } finally {
      setLoading(false);
    }
  };

  // Regrouper les photos par catégorie puis sous-catégorie
  const groupedPhotos: Record<string, Record<string, Photo[]>> = {};
  Object.values(PhotoCategorie).forEach((cat) => {
    groupedPhotos[cat] = {};
  });
  photos.forEach((photo) => {
    const cat = photo.categorie;
    const sousCat = photo.sousCategorie || 'Sans sous-catégorie';
    if (!groupedPhotos[cat][sousCat]) groupedPhotos[cat][sousCat] = [];
    groupedPhotos[cat][sousCat].push(photo);
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <span style={{ fontSize: 36, marginRight: 8 }}>🏫</span>
          <span style={{ fontWeight: 700 }}>{etablissement.nom}</span>
        </Typography>
        <Box
          sx={{
            bgcolor: 'grey.400',
            color: 'white',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 180,
            height: 28,
            cursor: 'pointer',
            boxShadow: 3,
            gap: 1.5,
            mt: 0.5,
            '&:hover': { bgcolor: '#cfa53a' },
            transition: 'background 0.2s',
            userSelect: 'none',
          }}
          onClick={handleAddPhoto}
        >
          <CameraIcon sx={{ fontSize: 20 , color: 'white' }} />
          <Typography sx={{ fontWeight: 400, fontSize: 14, letterSpacing: 0.5 , color: 'white' }}>
            Ajouter des photos
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {Object.values(PhotoCategorie).map((cat) => (
            <Box key={cat} sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 2,
                  color: '#fff',
                  bgcolor: 'primary.main',
                  px: 2,
                  py: 0.5,
                  borderRadius: 2,
                  display: 'inline-block',
                  fontWeight: 400
                }}
              >
                Galerie pour : {cat}
              </Typography>
              {Object.keys(groupedPhotos[cat]).length > 0 ? (
                Object.keys(groupedPhotos[cat]).map((sousCat) => (
                  <Box key={sousCat} sx={{ mb: 2 }}>
                    {sousCat !== 'Sans sous-catégorie' && (
                      <Typography variant="subtitle1" sx={{ mb: 1, ml: 1, color: 'text.secondary' }}>{sousCat}</Typography>
                    )}
                    <Grid container spacing={2}>
                      {groupedPhotos[cat][sousCat].map((photo) => (
                        <Grid item xs={12} sm={6} md={4} key={photo.id}>
                          <Card>
                            <CardMedia
                              component="img"
                              height="200"
                              image={photo.photoUrl}
                              alt={photo.legende}
                              sx={{ objectFit: 'cover' }}
                            />
                            <CardContent>
                              <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                Légende : {photo.legende}
                              </Typography>
                              {photo.description && (
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  Description : {photo.description}
                                </Typography>
                              )}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Chip label={photo.categorie} size="small" />
                                <Box>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditPhoto(photo)}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeletePhoto(photo.id)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">Aucune photo</Typography>
              )}
            </Box>
          ))}
        </Box>
      )}

      {!loading && photos.length === 0 && (
        <Box sx={{ textAlign: 'center', p: 3 }}>
          <Typography variant="body1" color="text.secondary">
            Aucune photo pour cet établissement
          </Typography>
        </Box>
      )}

      {/* Dialog pour ajouter/éditer une photo */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          {editingPhoto ? 'Modifier la photo' : 'Ajouter une photo'}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Catégorie *</InputLabel>
            <Select
              inputRef={categorieRef}
              value={formData.categorie}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                categorie: e.target.value as PhotoCategorie,
                sousCategorie: '', // reset sous-catégorie si changement
              }))}
              label="Catégorie *"
            >
              {Object.values(PhotoCategorie).map((categorie) => (
                <MenuItem key={categorie} value={categorie}>
                  {categorie}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {formData.categorie === PhotoCategorie.BATIMENT && (
            <TextField
              fullWidth
              label="Sous-catégorie"
                value={formData.sousCategorie}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  sousCategorie: e.target.value 
                }))}
              sx={{ mb: 2 }}
              placeholder="Saisir la sous-catégorie librement"
            />
          )}
          <TextField
            fullWidth
            label="Légende *"
            value={formData.legende}
            onChange={(e) => setFormData(prev => ({ 
              ...prev, 
              legende: e.target.value 
            }))}
            sx={{ mb: 2 }}
            required
          />
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ 
              ...prev, 
              description: e.target.value 
            }))}
            multiline
            rows={3}
            sx={{ mb: 2 }}
          />
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Sélectionner une photo
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<CameraIcon />}
                onClick={isMobileDevice() ? handlePhotoCapture : handleWebcamCapture}
              >
                {isMobileDevice() ? 'Prendre une photo' : 'Utiliser la webcam'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<PhotoLibraryIcon />}
                onClick={handlePhotoSelect}
              >
                Choisir depuis la galerie
              </Button>
            </Box>
            {formData.photoFiles && formData.photoFiles.length > 0 && (
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {formData.photoFiles.map((file, idx) => (
                    <Box key={idx} sx={{ position: 'relative', display: 'inline-block' }}>
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Aperçu ${idx + 1}`}
                        style={{ maxWidth: '100px', maxHeight: '100px', objectFit: 'cover', borderRadius: 4 }}
                      />
                      <IconButton
                        size="small"
                        sx={{ position: 'absolute', top: 0, right: 0, bgcolor: 'rgba(255,255,255,0.7)' }}
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          photoFiles: prev.photoFiles?.filter((_, i) => i !== idx)
                        }))}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            Annuler
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={loading || !formData.legende.trim() || !formData.photoFiles || formData.photoFiles.length === 0}
          >
            {loading ? <CircularProgress size={20} /> : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}; 